import { useEffect, useMemo, useState } from 'react';
import { useApiHolder } from '@backstage/core-plugin-api';
import { entityPresentationApiRef } from '@backstage/plugin-catalog-react';

/**
 * Entities by the names the portal shows for them — a team from the GitHub
 * organisation (DX-202) names itself in its profile — through the presentation
 * API, one per reference and in its order. A name is undefined until the API
 * has answered, and where there is no API to ask, so the caller chooses what
 * stands in meanwhile.
 *
 * Where a table also searches and sorts by the name, it has to hold this
 * string rather than render `EntityDisplayName`, or it finds and orders rows
 * by something other than what it shows.
 */
export function useEntityNames(refs: string[]): (string | undefined)[] {
  const presentation = useApiHolder().get(entityPresentationApiRef);
  const key = refs.join('|');
  const [answer, setAnswer] = useState<{
    key: string;
    names: (string | undefined)[];
  }>({ key: '', names: [] });
  const pending = useMemo(
    () => (key ? key.split('|') : []).map(() => undefined),
    [key],
  );
  useEffect(() => {
    if (!presentation || !key) return undefined;
    let active = true;
    Promise.all(
      key.split('|').map(ref =>
        presentation.forEntity(ref).promise.then(
          p => p.primaryTitle,
          () => undefined,
        ),
      ),
    ).then(names => {
      if (active) setAnswer({ key, names });
    });
    return () => {
      active = false;
    };
  }, [presentation, key]);
  return answer.key === key ? answer.names : pending;
}
